import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import type { CreateShareholderLoanInput } from '@accounting/shared';
import { useCreateShareholderLoan } from '../../hooks/useShareholderLoans';
import { useAccounts } from '../../hooks/useAccounts';
import { useActiveCompanyId } from '../../stores/useCompanyStore';
import { ApiError } from '../../lib/api';
import { LoanBuilder, type LoanBuilderValues } from '../../components/loans/LoanBuilder';

export const Route = createFileRoute('/_auth/shareholder-loans/new')({
  component: NewShareholderLoanPage,
});

function NewShareholderLoanPage() {
  const companyId = useActiveCompanyId();
  const navigate = useNavigate();
  const { data: accounts = [] } = useAccounts(companyId);
  const create = useCreateShareholderLoan(companyId);

  if (!companyId) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 32 }}>
        <p className="muted">No active company.</p>
        <Link to="/companies" className="btn-primary" style={{ display: 'inline-block', textDecoration: 'none' }}>Go to Companies</Link>
      </div>
    );
  }

  const errorMessage =
    create.error instanceof ApiError
      ? (create.error.body as { error?: string } | null)?.error ?? 'Could not create loan'
      : create.error?.message;

  const submit = (v: LoanBuilderValues) => {
    const input: CreateShareholderLoanInput = {
      name: v.name,
      borrowerContactId: v.contactId ?? undefined,
      receivableAccountId: v.principalAccountId,
      interestIncomeAccountId: v.interestAccountId,
      depositAccountId: v.bankAccountId ?? undefined,
      autoPostPayments: v.autoPostPayments,
      funding: v.funding,
      principal: v.terms.principal,
      annualRatePct: v.terms.annualRatePct,
      rateBasis: v.terms.rateBasis,
      paymentFrequency: v.terms.paymentFrequency,
      amortizationPeriods: v.terms.amortizationPeriods,
      numberOfPayments: v.terms.numberOfPayments,
      paymentAmount: v.terms.paymentAmount ?? undefined,
      originationDate: v.terms.originationDate,
      firstPaymentDate: v.terms.firstPaymentDate,
      businessDayRule: v.terms.businessDayRule,
      lateChargePct: v.terms.lateChargePct ?? undefined,
      lateChargeGraceDays: v.terms.lateChargeGraceDays ?? undefined,
      defaultRateSpreadPct: v.terms.defaultRateSpreadPct ?? undefined,
    };
    create.mutate(input, { onSuccess: (l) => void navigate({ to: '/shareholder-loans/$id', params: { id: l.id } }) });
  };

  return (
    <div>
      <Link to="/shareholder-loans" style={{ fontSize: 13 }}>← Shareholder loans</Link>
      <h1 style={{ fontSize: 24, margin: '8px 0 4px' }}>New shareholder loan</h1>
      <p className="muted" style={{ margin: '0 0 16px', fontSize: 13 }}>
        A note the company holds: the balance is a receivable, and the interest portion of each repayment is income.
      </p>
      <LoanBuilder
        side="receivable"
        companyId={companyId}
        accounts={accounts}
        onSubmit={submit}
        isPending={create.isPending}
        errorMessage={errorMessage}
        cancelHref="/shareholder-loans"
      />
    </div>
  );
}
